import { useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react'
import { buildCategoryPathFromTrail, getCategoryImageSources } from '../../routing'
import type {
  CatalogCategory,
  CatalogCategoryNode,
  CategoryRouteData,
} from '../../types'

type Props = {
  children: CategoryRouteData['children']
  trail: CategoryRouteData['trail']
  toLocalizedPath: (path: string) => string
  onSectionChange?: (sectionId: string) => void
}

export const buildCategorySectionId = (category: CatalogCategory): string => {
  const source = (category.slug || category._id || 'categoria').trim().toLowerCase()
  const safeSource = source.replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '')
  return `categoria-${safeSource || 'categoria'}`
}

const getLabelInitials = (label: string): string =>
  label
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')

const resolveCategoryName = (category: CatalogCategory): string =>
  (category.name || category.slug || 'Categoria').trim() || 'Categoria'

const resolveCategoryDescription = (category: CatalogCategory): string => (category.description || '').trim()

const resolveCategoryPath = (category: CatalogCategory, trail: CategoryRouteData['trail']) =>
  buildCategoryPathFromTrail([...trail, category])

const useCategoryImage = (category: CatalogCategory) => {
  const image = getCategoryImageSources(category, '(min-width: 1024px) 45vw, 92vw')
  const [imageUnavailable, setImageUnavailable] = useState(!image.src)

  useEffect(() => {
    setImageUnavailable(!image.src)
  }, [image.src])

  return { image, imageUnavailable, setImageUnavailable }
}

const CategoryImageCard = ({
  category,
  trail,
  toLocalizedPath,
  withSectionId = true,
}: {
  category: CatalogCategoryNode
  trail: CategoryRouteData['trail']
  toLocalizedPath: (path: string) => string
  withSectionId?: boolean
}) => {
  const { image, imageUnavailable, setImageUnavailable } = useCategoryImage(category)
  const name = resolveCategoryName(category)
  const description = resolveCategoryDescription(category)
  const sectionId = buildCategorySectionId(category)
  const placeholderInitials = useMemo(() => getLabelInitials(name), [name])
  const hasImage = !imageUnavailable

  const card = (
    <a
        href={toLocalizedPath(resolveCategoryPath(category, trail))}
        className="group relative block aspect-[1.9/1] overflow-hidden rounded-[1.5rem] bg-slate-100 shadow-sm transition hover:-translate-y-0.5 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2"
      >
        {imageUnavailable ? (
          <div className="absolute inset-0 grid place-items-center bg-slate-100 text-slate-400">
            <span className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-lg font-bold tracking-wide text-slate-500">
              {placeholderInitials || 'CAT'}
            </span>
          </div>
        ) : (
          <img
            src={image.src}
            srcSet={image.srcSet}
            sizes={image.sizes}
            alt={name}
            loading="lazy"
            decoding="async"
            fetchPriority="low"
            onError={() => setImageUnavailable(true)}
            className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-[1.035]"
          />
        )}
        {hasImage ? <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-900/35 to-transparent" /> : null}
        <div className={`absolute inset-x-0 bottom-0 space-y-2 p-5 sm:p-6 ${hasImage ? 'text-white' : 'text-slate-900'}`}>
          <h3 className="text-xl font-black tracking-tight sm:text-2xl">{name}</h3>
          {description ? <p className={`line-clamp-3 text-sm leading-relaxed ${hasImage ? 'text-white/90' : 'text-slate-600'}`}>{description}</p> : null}
        </div>
      </a>
  )

  return withSectionId ? <article id={sectionId} className="scroll-mt-32">{card}</article> : card
}

const NestedCategorySection = ({
  category,
  trail,
  toLocalizedPath,
}: {
  category: CatalogCategoryNode
  trail: CategoryRouteData['trail']
  toLocalizedPath: (path: string) => string
}) => {
  const name = resolveCategoryName(category)
  const description = resolveCategoryDescription(category)
  const sectionId = buildCategorySectionId(category)

  return (
    <section id={sectionId} aria-labelledby={`${sectionId}-title`} className="scroll-mt-32 space-y-5 sm:space-y-6">
      <header className="space-y-2 text-center">
        <h3 id={`${sectionId}-title`} className="text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
          {name}
        </h3>
        {description ? <p className="mx-auto max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">{description}</p> : null}
      </header>
      <div className="grid gap-4 sm:gap-5 md:grid-cols-2">
        {category.children.map((child) => (
          <CategoryImageCard
            key={child._id}
            category={child}
            trail={[...trail, category]}
            toLocalizedPath={toLocalizedPath}
          />
        ))}
      </div>
    </section>
  )
}

const CategoryHubSections = ({
  children,
  trail,
  toLocalizedPath,
  onSectionChange,
}: Props) => {
  const leafChildren = useMemo(() => children.filter((child) => child.children.length === 0), [children])
  const nestedChildren = useMemo(() => children.filter((child) => child.children.length > 0), [children])
  const sectionIds = useMemo(() => children.map(buildCategorySectionId), [children])
  const [activeSectionId, setActiveSectionId] = useState(sectionIds[0] ?? '')

  const scrollToSection = useCallback((sectionId: string, behavior: ScrollBehavior = 'smooth') => {
    const target = document.getElementById(sectionId)
    if (!target) return false
    const offset = 112
    const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - offset)
    window.scrollTo({ top, behavior })
    return true
  }, [])

  useEffect(() => {
    setActiveSectionId(sectionIds[0] ?? '')
  }, [sectionIds])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const scrollToCurrentHash = (behavior: ScrollBehavior = 'smooth') => {
      const sectionId = window.location.hash.replace(/^#/, '')
      if (!sectionId) return
      window.requestAnimationFrame(() => {
        if (scrollToSection(sectionId, behavior)) {
          setActiveSectionId(sectionId)
          onSectionChange?.(sectionId)
        }
      })
    }

    const handleHashChange = () => scrollToCurrentHash()
    const handlePopState = () => scrollToCurrentHash()
    scrollToCurrentHash('auto')
    window.addEventListener('hashchange', handleHashChange)
    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('hashchange', handleHashChange)
      window.removeEventListener('popstate', handlePopState)
    }
  }, [onSectionChange, scrollToSection, sectionIds])

  useEffect(() => {
    if (typeof window === 'undefined' || !sectionIds.length || !('IntersectionObserver' in window)) return
    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter((section): section is HTMLElement => Boolean(section))
    if (!sections.length) return

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((left, right) => right.intersectionRatio - left.intersectionRatio)
        if (visible[0]?.target.id) setActiveSectionId(visible[0].target.id)
      },
      { rootMargin: '-18% 0px -62% 0px', threshold: [0.1, 0.35, 0.7] },
    )

    sections.forEach((section) => observer.observe(section))
    return () => observer.disconnect()
  }, [sectionIds])

  const handleAnchorClick = (event: MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    event.preventDefault()
    const nextHash = `#${sectionId}`
    const nextUrl = `${window.location.pathname}${window.location.search}${nextHash}`
    if (window.location.hash !== nextHash) window.history.pushState({}, '', nextUrl)
    setActiveSectionId(sectionId)
    onSectionChange?.(sectionId)
    scrollToSection(sectionId)
  }

  return (
    <div className="space-y-8 sm:space-y-12">
      <nav aria-label="Sottocategorie" className="sticky top-3 z-20 bg-[color:var(--theme-color-background,#f8fafc)]/95 py-3 backdrop-blur-md">
        <div className="flex gap-2 overflow-x-auto px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {children.map((child) => {
            const sectionId = buildCategorySectionId(child)
            const label = resolveCategoryName(child)
            const isActive = sectionId === activeSectionId
            return (
              <a
                key={child._id}
                href={`#${sectionId}`}
                aria-current={isActive ? 'location' : undefined}
                onClick={(event) => handleAnchorClick(event, sectionId)}
                className={`shrink-0 rounded-full border px-4 py-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2 ${
                  isActive
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-slate-300 bg-white text-slate-700 hover:border-slate-900 hover:text-slate-900'
                }`}
              >
                {label}
              </a>
            )
          })}
        </div>
      </nav>

      {leafChildren.length ? (
        <div className="grid gap-4 sm:gap-5 md:grid-cols-2">
          {leafChildren.map((child) => (
            <CategoryImageCard
              key={child._id}
              category={child}
              trail={trail}
              toLocalizedPath={toLocalizedPath}
            />
          ))}
        </div>
      ) : null}

      {nestedChildren.map((child) => (
        <NestedCategorySection
          key={child._id}
          category={child}
          trail={trail}
          toLocalizedPath={toLocalizedPath}
        />
      ))}
    </div>
  )
}

export default CategoryHubSections
